/**
 * Sleep-window evaluator — decides whether the screen should be asleep at a
 * given moment, and how far through the sleep window it is.
 *
 * Reuses the schedule evaluator's timezone helper so the sleep window is
 * evaluated in the manifest's declared timezone, exactly like schedule slots
 * (#42): the Pi's local clock is not trusted. Supports midnight-crossing
 * windows (e.g. 22:00–06:30), same as slots.
 */

import type { SleepWindow } from '@dooh/shared';
import { getTimeInTimezone } from '../playback/schedule-evaluator';

export interface SleepWindowEvaluation {
  active: boolean;
  /** 0–1 progress through the window (0 when inactive). */
  progress: number;
  /** Wake time label ("HH:MM") — the window's endTime. */
  wakeAt: string;
}

/** "HH:MM" → minutes since midnight. */
function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':');
  return Number(h) * 60 + Number(m);
}

const MINUTES_PER_DAY = 24 * 60;

/**
 * Evaluate a sleep window at `now` in `timezone`.
 *
 * A window is active when the current ISO day-of-week (1=Mon … 7=Sun) is in
 * daysOfWeek and the current wall-clock time falls inside [startTime,
 * endTime), with midnight-crossing windows matching after startTime OR
 * before endTime (mirroring isSlotActive). Progress is the fraction of the
 * window elapsed, used for the overlay's time-until-wake bar.
 */
export function evaluateSleepWindow(
  window: SleepWindow,
  now: Date,
  timezone?: string,
): SleepWindowEvaluation {
  const { dayOfWeek, timeString } = timezone
    ? getTimeInTimezone(now, timezone)
    : {
        dayOfWeek: now.getDay() === 0 ? 7 : now.getDay(),
        timeString: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
      };

  const start = toMinutes(window.startTime);
  const end = toMinutes(window.endTime);
  const current = toMinutes(timeString);

  const crossesMidnight = start > end;
  const active =
    window.daysOfWeek.includes(dayOfWeek) &&
    (crossesMidnight ? current >= start || current < end : current >= start && current < end);

  if (!active) {
    return { active: false, progress: 0, wakeAt: window.endTime };
  }

  const total = crossesMidnight ? MINUTES_PER_DAY - start + end : end - start;
  const elapsed = crossesMidnight
    ? current >= start
      ? current - start
      : MINUTES_PER_DAY - start + current
    : current - start;

  const progress = total > 0 ? Math.min(1, Math.max(0, elapsed / total)) : 0;
  return { active: true, progress, wakeAt: window.endTime };
}
